import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import StationThemeLayout from "../../../layouts/StationThemeLayout";
import type { RootState } from "../../../redux/store";
import { setUploadedImageUrl } from "../../../redux/slices/uploadFlowSlice";
import { removeBackgroundWithFreepik } from "../../../services/freepikBgRemovalService";
import { uploadTemporaryImage } from "../../../services/fileUploadService";
import { persistFavouriteTransparencyFlag } from "../../../services/caricatureHobbyService";
import { readSelectedAiSelfieArtFromStorage } from "../../../services/stylePromptResolver";
import {
  readActiveStoredStation,
  resolveStationRouteSlug,
} from "../../../utils/stationFlowStorage";
import Loader from "../../../compoment/ui/Loader";

/**
 * Print Your Own / snap-print: after image-transformation Submit/Skip.
 * Yes → remove-bg API, upload cutout, then generate with is_transparent=true.
 * No  → generate with original photo (opaque).
 */
const BgRemove = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug: slugParam } = useParams<{ slug: string }>();
  const dispatch = useDispatch();
  const uploadFlow = useSelector(
    (s: RootState) =>
      s.uploadFlow || { uploadedImageUrl: null, processedImageUrl: null }
  );

  const [busy, setBusy] = useState(false);

  const stationData = useMemo(() => {
    const fromNav = (location.state as { stationData?: Record<string, unknown> } | null)
      ?.stationData;
    if (fromNav && typeof fromNav === "object") return fromNav;
    return readActiveStoredStation() || {};
  }, [location.state]);

  const customLine = useMemo(() => {
    const fromNav = (location.state as { customLine?: string } | null)?.customLine;
    if (typeof fromNav === "string") return fromNav.trim();
    try {
      return (localStorage.getItem("transformationCustomLine") || "").trim();
    } catch {
      return "";
    }
  }, [location.state]);

  const selectedArt = useMemo(() => readSelectedAiSelfieArtFromStorage(), []);
  const styleSlug =
    resolveStationRouteSlug(stationData, slugParam, selectedArt) ||
    slugParam ||
    "print-your-own";

  const imageFromNav = useMemo(() => {
    const fromNav = (location.state as { uploadedImageUrl?: string } | null)
      ?.uploadedImageUrl;
    return typeof fromNav === "string" ? fromNav.trim() : "";
  }, [location.state]);

  const sourceImage = (
    imageFromNav ||
    uploadFlow.uploadedImageUrl ||
    uploadFlow.processedImageUrl ||
    ""
  )
    .toString()
    .trim();

  useEffect(() => {
    if (!imageFromNav) return;
    if (uploadFlow.uploadedImageUrl === imageFromNav) return;
    dispatch(setUploadedImageUrl(imageFromNav));
  }, [dispatch, imageFromNav, uploadFlow.uploadedImageUrl]);

  const goToAiTransformation = (imageUrl: string, bgRemoved: boolean) => {
    const nextUrl = (imageUrl || sourceImage || "").trim();
    if (!nextUrl) return;

    persistFavouriteTransparencyFlag(bgRemoved);
    dispatch(setUploadedImageUrl(nextUrl));
    navigate(`/${styleSlug}/image-transformation`, {
      replace: true,
      state: {
        stationData,
        customLine,
        uploadedImageUrl: nextUrl,
        bgRemoveDecided: true,
        bgRemoved,
        skipPrompt: true,
      },
    });
  };

  const handleNo = () => {
    if (busy || !sourceImage) return;
    goToAiTransformation(sourceImage, false);
  };

  const handleYesRemoveBg = async () => {
    if (!sourceImage || busy) return;
    setBusy(true);
    try {
      // 1) Remove background first.
      const bgRemoved = await removeBackgroundWithFreepik(sourceImage);
      if (!bgRemoved?.trim()) {
        toast.error("Background removal failed. Please try again.");
        setBusy(false);
        return;
      }

      // 2) Persist cutout as a public URL for generate-character-web.
      let finalUrl = bgRemoved.trim();
      const uploaded = await uploadTemporaryImage(finalUrl);
      if (uploaded.success && uploaded.url?.trim()) {
        finalUrl = uploaded.url.trim();
      } else if (!/^https?:\/\//i.test(finalUrl) && !finalUrl.startsWith("data:")) {
        toast.error(uploaded.error || "Failed to upload cutout image.");
        setBusy(false);
        return;
      }

      // 3) Continue to AI transform with is_transparent=true.
      goToAiTransformation(finalUrl, true);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Background removal failed";
      console.error(message, err);
      toast.error(message);
      setBusy(false);
    }
  };

  return (
    <StationThemeLayout
      className="caricatureTransformationSection is-prompt-step bgRemoveSection"
      station={
        stationData as { backgroundImageUrl?: string; type?: string; name?: string }
      }
      showStationLogo
      showBack={!busy}
      onBack={() =>
        navigate(`/${styleSlug}/image-transformation`, {
          state: { stationData, customLine },
        })
      }
    >
      {busy && <Loader />}
      <div className="imgSketchContainer mt-5">
        <div className="imgSection imgSectionTransformation">
          <div
            className="transformationStage transformationPhase-1 is-prompt-idle"
            data-phase={1}
          >
            <div className="transformationImageWrap">
              {sourceImage ? (
                <img
                  src={sourceImage}
                  alt="Your photo"
                  className="caricatureImg transformationPreview"
                  draggable={false}
                />
              ) : (
                <div className="bgRemoveImagePlaceholder">No photo found</div>
              )}
            </div>
          </div>

          <div className="transformationPromptStep bgRemovePromptStep">
            <div className="transformationPromptHeading">
              Do you want to remove the background?
            </div>
            <div className="bgRemoveActions">
              <button
                type="button"
                className="btnResultYes"
                disabled={busy || !sourceImage}
                onClick={handleYesRemoveBg}
              >
                Yes
              </button>
              <button
                type="button"
                className="btnResultNo"
                disabled={busy || !sourceImage}
                onClick={handleNo}
              >
                No
              </button>
            </div>
          </div>
        </div>
      </div>
    </StationThemeLayout>
  );
};

export default BgRemove;
